/**
 * Crew van een draaidag: gekoppelde Personen-records (Airtable 'Naam (from Crew)') samengevoegd met de namen
 * uit de titel van de Outlook-afspraak.
 *
 * Waarom: de Airtable-automation koppelt crew via de Outlook-genodigden. Externe crew (freelancers zonder
 * Full Frame-account, in de titel gemarkeerd met "(b)" = bevestigd of "(g)" = gevraagd) wordt niet uitgenodigd
 * en komt dus nooit in het Crew-veld. De titel "Raijmond + Paul (b) + Enya || …" is dan de enige bron voor Paul
 * (Robin, 22 sep 2026). Zonder deze samenvoeging toonde de tool "Raijmond + Enya" en leek de sync kapot.
 */

const SCHEIDERS = /\s*(?:\|\||\/\/|\|)\s*|\s*:\s*|\s+\/\s+/;
const PREFIX = /^(optie|studio|locatiebezoek|recce|opname|draaidag)\b\s*(\([^)]*\))?\s*:?\s*/i;
const TIJDVAK = /^\(?\s*van\s+\d{1,2}[:.]\d{2}\s+tot\s+\d{1,2}[:.]\d{2}\s*\)?\s*/i;
const MARKERING = /\(\s*([a-z?])\s*\)|\b([bg])\b(?=\s*$)/i;
const STATUS = { b: 'bevestigd', g: 'gevraagd', '?': 'onzeker' };

const isNaam = (v) => typeof v === 'string' && v.trim() && !v.startsWith('rec');
const voornaam = (v) => String(v || '').trim().toLowerCase().split(/\s+/)[0];

/** Namen uit het deel vóór "||" van de titel: [{ naam, status }] */
export function crewUitTitel(titel) {
  let kop = String(titel || '');
  if (!kop.trim()) return [];
  kop = kop.replace(TIJDVAK, '').replace(PREFIX, '').replace(/^[\s/:|-]+/, '');
  const delen = kop.split(SCHEIDERS);
  kop = delen[0] || '';
  // zonder scheider is de hele titel vaak "Projectnaam - [nummer]" → geen crew
  if (delen.length === 1 && /\[\d+\]|\s-\s/.test(kop)) return [];
  kop = kop.replace(/^\(.*?\)\s*/, '');
  return kop
    .split(/\s*\+\s*|\s*,\s*|\s+en\s+|\s*&\s*/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      const m = t.match(MARKERING);
      const status = m ? STATUS[(m[1] || m[2] || '').toLowerCase()] || null : null;
      const naam = t.replace(/\(.*?\)/g, '').replace(/[?]/g, '').replace(/\s+[bg]$/i, '').trim();
      return { naam, status };
    })
    .filter((x) => x.naam && x.naam.length <= 30 && !/\d{3,}/.test(x.naam) && !/^(camera|crew|team|ntb|tbd)\b/i.test(x.naam));
}

/**
 * Gekoppelde crew + titelnamen die niet gekoppeld zijn.
 * @returns {{ gekoppeld: string[], nietGekoppeld: {naam:string,status:string|null}[], alle: string[], label: string }}
 */
export function crewSamengevoegd(gekoppeldRaw, titel) {
  const gekoppeld = (Array.isArray(gekoppeldRaw) ? gekoppeldRaw : gekoppeldRaw ? [gekoppeldRaw] : []).filter(isNaam).map((s) => s.trim());
  const bekend = new Set(gekoppeld.map(voornaam));
  const nietGekoppeld = crewUitTitel(titel).filter((x) => !bekend.has(voornaam(x.naam)));
  const extra = nietGekoppeld.map((x) => (x.status === 'bevestigd' ? `${x.naam} (b)` : x.status === 'gevraagd' ? `${x.naam} (g)` : x.status === 'onzeker' ? `${x.naam}?` : x.naam));
  const alle = [...gekoppeld, ...extra];
  return { gekoppeld, nietGekoppeld, alle, label: alle.join(' + ') };
}

export default crewSamengevoegd;
